import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Space, Form, Select, DatePicker, Input, InputNumber, message, Typography, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api.service';
import assetService, { type AssetDto } from '@/services/asset.service';
import assetDisposalService, { type DisposalEvent, type DisposalPreview } from '@/services/asset-disposal.service';
import assetAccountingDefaultsService from '@/services/asset-accounting-defaults.service';
import { buildAssetIdToLabelMap, formatAssetOption, formatJournalVoucherLabel, resolveAssetDisplayFromMap } from '@/utils/assetDisplay';

const { Text } = Typography;
const { TextArea } = Input;

interface CoaRow {
  id: string;
  account_code: string;
  account_name: string;
  is_group: boolean;
  account_type: string;
}

const AssetDisposalPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin || user?.permissions?.includes('asset.disposal.view') || user?.permissions?.includes('asset.view')
  );
  const canRun = Boolean(
    user?.is_super_admin || user?.permissions?.includes('asset.disposal.run') || user?.permissions?.includes('asset.edit')
  );

  const [form] = Form.useForm();
  const [preview, setPreview] = useState<DisposalPreview | null>(null);
  const assetIdWatch = Form.useWatch('asset_id', form);

  const { data: assets = [] } = useQuery({
    queryKey: ['assets', 'disposal-pick'],
    queryFn: () => assetService.list(),
    enabled: canView,
  });
  const assetLabels = useMemo(() => buildAssetIdToLabelMap(assets), [assets]);

  const disposable = useMemo(
    () => assets.filter((a) => a.status !== 'disposed' && a.status !== 'draft'),
    [assets]
  );

  const { data: coa = [] } = useQuery({
    queryKey: ['coa', 'disposal'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const data = (res as { data?: unknown })?.data ?? res;
      return Array.isArray((data as { data?: CoaRow[] })?.data)
        ? (data as { data: CoaRow[] }).data
        : Array.isArray(data)
          ? (data as CoaRow[])
          : [];
    },
    enabled: canView,
  });

  const leafAccounts = useMemo(() => coa.filter((a) => !a.is_group), [coa]);

  const { data: defaults } = useQuery({
    queryKey: ['asset-accounting-defaults'],
    queryFn: () => assetAccountingDefaultsService.get(),
    enabled: canView,
  });

  useEffect(() => {
    if (!defaults) return;
    form.setFieldsValue({
      accumulated_depreciation_account_id: defaults.accumulated_depreciation_account_id ?? undefined,
      asset_cost_account_id: defaults.asset_cost_account_id ?? undefined,
      proceeds_account_id: defaults.disposal_proceeds_account_id ?? undefined,
      gain_loss_account_id:
        defaults.disposal_gain_account_id ?? defaults.disposal_loss_account_id ?? undefined,
    });
  }, [defaults, form]);

  useEffect(() => {
    if (!assetIdWatch) return;
    void assetService
      .resolveGl(assetIdWatch)
      .then((gl) => {
        form.setFieldsValue({
          accumulated_depreciation_account_id: gl.accumulated_depreciation_account_id ?? undefined,
          asset_cost_account_id: gl.asset_cost_account_id ?? undefined,
          proceeds_account_id: gl.disposal_proceeds_account_id ?? undefined,
          gain_loss_account_id: gl.disposal_gain_account_id ?? gl.disposal_loss_account_id ?? undefined,
        });
      })
      .catch(() => {
        /* keep company defaults */
      });
  }, [assetIdWatch, form]);

  const { data: events = [], isLoading: le } = useQuery({
    queryKey: ['asset-disposal-events'],
    queryFn: () => assetDisposalService.listEvents(),
    enabled: canView,
  });

  const previewMut = useMutation({
    mutationFn: (args: { asset_id: string; proceeds: number }) => assetDisposalService.preview(args),
    onSuccess: (data) => {
      setPreview(data);
      if (defaults) {
        const plAccount =
          data.gain_loss >= 0
            ? defaults.disposal_gain_account_id ?? defaults.disposal_loss_account_id
            : defaults.disposal_loss_account_id ?? defaults.disposal_gain_account_id;
        if (plAccount) {
          form.setFieldValue('gain_loss_account_id', plAccount);
        }
      }
      message.success('Preview ready');
    },
    onError: (e: Error) => {
      setPreview(null);
      message.error(e?.message || 'Preview failed');
    },
  });

  const execMut = useMutation({
    mutationFn: (body: Parameters<typeof assetDisposalService.execute>[0]) => assetDisposalService.execute(body),
    onSuccess: (d) => {
      message.success(`Posted. ${d.journal_voucher_number}`);
      setPreview(null);
      form.resetFields();
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['asset-disposal-events'] });
    },
    onError: (e: Error) => message.error(e?.message || 'Disposal failed'),
  });

  const runPreview = () => {
    if (!assetIdWatch) {
      message.warning('Select an asset first');
      return;
    }
    const proceeds = form.getFieldValue('proceeds');
    void previewMut.mutate({ asset_id: assetIdWatch, proceeds: proceeds != null ? Number(proceeds) : 0 });
  };

  const eventCols: ColumnsType<DisposalEvent> = [
    { title: '#', dataIndex: 'disposal_number', key: 'n', width: 130 },
    {
      title: 'Asset',
      key: 'a',
      ellipsis: true,
      render: (_: unknown, r) => resolveAssetDisplayFromMap(assetLabels, r.asset_id),
    },
    { title: 'Type', dataIndex: 'disposal_type', key: 't', width: 100 },
    {
      title: 'Date',
      dataIndex: 'disposal_date',
      key: 'd',
      width: 120,
      render: (d: string) => (d ? d.slice(0, 10) : '—'),
    },
    {
      title: 'P/L',
      dataIndex: 'gain_loss',
      key: 'g',
      width: 100,
      render: (n: number | null) => (n == null ? '—' : Number(n).toFixed(2)),
    },
    {
      title: 'Journal',
      dataIndex: 'journal_voucher_id',
      key: 'j',
      ellipsis: true,
      render: (id: string | null) => (id ? <Tag>{formatJournalVoucherLabel(id)}</Tag> : '—'),
    },
  ];

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>Access denied. Need asset.view or asset.disposal.view</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 8 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/assets')}>
        Back to assets
      </Button>

      <Card title="Asset disposal" style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Clears accumulated depreciation and cost from the books, books proceeds, and records gain/loss. Asset must have purchase value. Four distinct GL
          accounts are required. Prefills from{' '}
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate('/asset/accounting-defaults')}>
            Asset Accounting Defaults
          </Button>
          .
        </Text>

        {canRun && (
          <Form
            form={form}
            layout="vertical"
            initialValues={{ disposal_type: 'sale', proceeds: 0 }}
            onFinish={(v) => {
              void execMut.mutate({
                asset_id: v.asset_id,
                disposal_type: v.disposal_type,
                disposal_date: (v.disposal_date as dayjs.Dayjs).format('YYYY-MM-DD'),
                proceeds: v.proceeds != null ? Number(v.proceeds) : 0,
                notes: v.notes || null,
                accumulated_depreciation_account_id: v.accumulated_depreciation_account_id,
                asset_cost_account_id: v.asset_cost_account_id,
                proceeds_account_id: v.proceeds_account_id,
                gain_loss_account_id: v.gain_loss_account_id,
              });
            }}
          >
            <Form.Item name="asset_id" label="Asset" rules={[{ required: true, message: 'Select an asset' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Choose asset (not draft/disposed)"
                options={disposable.map((a: AssetDto) => ({ value: a.id, label: `${a.asset_code} — ${a.asset_name} (${a.status})` }))}
              />
            </Form.Item>
            <Form.Item name="proceeds" label="Proceeds (sale)">
              <InputNumber min={0} style={{ width: '100%' }} onChange={() => setPreview(null)} />
            </Form.Item>
            <Form.Item>
              <Button onClick={runPreview} loading={previewMut.isPending}>
                Preview numbers
              </Button>
            </Form.Item>
            {preview && (
              <Card size="small" type="inner" style={{ marginBottom: 16 }} title="Preview">
                <Space direction="vertical">
                  <Text>NBV: {preview.nbv.toFixed(2)} (cost {preview.cost.toFixed(2)}, accum. depr. {preview.ad.toFixed(2)})</Text>
                  <Text>Proceeds: {preview.proceeds.toFixed(2)}</Text>
                  <Text type={preview.gain_loss >= 0 ? 'success' : 'danger'}>
                    Gain / (loss): {preview.gain_loss.toFixed(2)}
                  </Text>
                </Space>
              </Card>
            )}
            <Form.Item name="disposal_type" label="Disposal type" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'sale', label: 'Sale' },
                  { value: 'scrap', label: 'Scrap' },
                  { value: 'write_off', label: 'Write-off' },
                ]}
              />
            </Form.Item>
            <Form.Item name="disposal_date" label="Disposal date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item
              name="accumulated_depreciation_account_id"
              label="Accumulated depreciation (debit to clear)"
              rules={[{ required: true, message: 'Select account' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={leafAccounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.account_name} (${a.account_type})` }))}
              />
            </Form.Item>
            <Form.Item name="asset_cost_account_id" label="Asset cost (credit to remove from books)" rules={[{ required: true, message: 'Select account' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={leafAccounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.account_name} (${a.account_type})` }))}
              />
            </Form.Item>
            <Form.Item name="proceeds_account_id" label="Proceeds / bank (debit; use 0-proceeds with a clearing if needed)" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={leafAccounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.account_name} (${a.account_type})` }))}
              />
            </Form.Item>
            <Form.Item name="gain_loss_account_id" label="Gain or loss on disposal (P&L)" rules={[{ required: true, message: 'Select account' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={leafAccounts.map((a) => ({ value: a.id, label: `${a.account_code} — ${a.account_name} (${a.account_type})` }))}
              />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <TextArea rows={2} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={execMut.isPending}>
              Post disposal
            </Button>
          </Form>
        )}

        <Card type="inner" title="Recent disposals" style={{ marginTop: 24 }}>
          <Table<DisposalEvent> size="small" rowKey="id" loading={le} dataSource={events} columns={eventCols} pagination={false} />
        </Card>
      </Card>
    </div>
  );
};

export default AssetDisposalPage;
